import React, { useEffect, useState, useRef } from 'react';
import Webcam from 'react-webcam';
import { CircleIcon, SquareIcon, RefreshCwIcon } from 'lucide-react';
interface VideoRecorderProps {
  audioOnly?: boolean;
  onSave: (blob: Blob) => void;
  onCancel: () => void;
}
export function VideoRecorder({
  audioOnly = false,
  onSave,
  onCancel
}: VideoRecorderProps) {
  const webcamRef = useRef<Webcam>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (recording) {
      interval = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [recording]);
  const handleStartRecording = () => {
    setRecordedChunks([]);
    setRecordingTime(0);
    if (webcamRef.current?.stream) {
      const mediaRecorder = new MediaRecorder(webcamRef.current.stream, {
        mimeType: 'video/webm'
      });
      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.addEventListener('dataavailable', event => {
        if (event.data.size > 0) {
          setRecordedChunks(prev => [...prev, event.data]);
        }
      });
      mediaRecorder.start();
      setRecording(true);
    }
  };
  const handleStopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };
  const handleSave = () => {
    if (recordedChunks.length > 0) {
      const blob = new Blob(recordedChunks, {
        type: 'video/webm'
      });
      onSave(blob);
    }
  };
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="space-y-4" data-id="element-15">
      <div className="relative aspect-video bg-black rounded-lg overflow-hidden" data-id="element-16">
        {recordedChunks.length === 0 ? <>
            <Webcam ref={webcamRef} audio={true} videoConstraints={{
          facingMode: facingMode
        }} className="w-full h-full object-cover" mirrored={facingMode === 'user'} data-id="element-17" />
            {recording && <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full font-semibold flex items-center gap-2" data-id="element-18">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse" data-id="element-19" />
                {formatTime(recordingTime)}
              </div>}
            <button onClick={() => setFacingMode(facingMode === 'user' ? 'environment' : 'user')} className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm p-3 rounded-full hover:bg-white/30 transition-all" data-id="element-20">
              <RefreshCwIcon className="w-5 h-5" data-id="element-21" />
            </button>
          </> : <video src={URL.createObjectURL(new Blob(recordedChunks, {
        type: 'video/webm'
      }))} controls className="w-full h-full" data-id="element-22" />}
      </div>
      <div className="flex gap-4 justify-center" data-id="element-23">
        {recordedChunks.length === 0 ? <>
            {!recording ? <>
                <button onClick={onCancel} className="px-6 py-3 bg-white/20 backdrop-blur-sm rounded-xl font-semibold hover:bg-white/30 transition-all" data-id="element-24">
                  Cancel
                </button>
                <button onClick={handleStartRecording} className="px-8 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-all flex items-center gap-2" data-id="element-25">
                  <CircleIcon className="w-5 h-5 fill-current" data-id="element-26" />
                  Start Recording
                </button>
              </> : <button onClick={handleStopRecording} className="px-8 py-3 bg-white text-rose-500 rounded-xl font-semibold hover:bg-opacity-90 transition-all flex items-center gap-2" data-id="element-27">
                <SquareIcon className="w-5 h-5 fill-current" data-id="element-28" />
                Stop Recording
              </button>}
          </> : <>
            <button onClick={() => setRecordedChunks([])} className="flex-1 px-6 py-3 bg-white/20 backdrop-blur-sm rounded-xl font-semibold hover:bg-white/30 transition-all" data-id="element-29">
              Re-record
            </button>
            <button onClick={handleSave} className="flex-1 px-6 py-3 bg-white text-rose-500 rounded-xl font-semibold hover:bg-opacity-90 transition-all" data-id="element-30">
              Use This Recording
            </button>
          </>}
      </div>
    </div>;
}